import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useSession } from '../context/SessionContext';
import { useCatalog } from '../context/CatalogContext';
import { fixImageUrl, buildBannerList } from '../lib/format';
import PageHeader from '../lib/ui/PageHeader';
import Button from '../lib/ui/Button';
import { Card } from '../lib/ui/Card';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function InfoRow({ icon, label, value, muted }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-black/5 last:border-0">
      <span className="text-lg w-6 text-center shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-ink-faint font-bold uppercase tracking-wide">{label}</div>
        <div className={`text-sm font-semibold ${muted ? 'text-ink-faint italic' : 'text-ink'}`}>{value}</div>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { lang } = useLang();
  const te = lang === 'te';
  const { sessionUser, onLogout } = useSession();
  const { activeSection } = useCatalog();
  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [logoutConf, setLogoutConf] = useState(false);
  const [copied, setCopied] = useState(false);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    if (!sessionUser) { navigate('/signup', { replace: true }); return; }
    if (sessionUser.token) {
      axios.get(`${API}/api/auth/referral`, { headers: { Authorization: `Bearer ${sessionUser.token}` } })
        .then((r) => setReferral(r.data)).catch(() => setReferral(null));
    }
  }, [sessionUser, navigate]);

  useEffect(() => {
    if (!API || !activeSection) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const q = `sectionId=${encodeURIComponent(activeSection)}`;
        const [offersRes, promosRes] = await Promise.all([
          axios.get(`${API}/api/offers?${q}`).catch(() => ({ data: { offers: [] } })),
          axios.get(`${API}/api/promos/active?${q}`).catch(() => ({ data: { promos: [] } })),
        ]);
        if (cancelled) return;
        setBanners(buildBannerList(offersRes.data?.offers, promosRes.data?.promos));
      } catch {
        if (!cancelled) setBanners([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSection]);

  if (!sessionUser) return null;
  const initials = (sessionUser.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title={te ? 'ప్రొఫైల్' : 'Profile'} fallback="/" />
      <div className="px-4 md:px-6 py-4">
        <div className="flex items-center gap-3.5 bg-gradient-to-br from-brand-dark to-brand rounded-2xl px-5 py-5 text-white mb-4">
          <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center font-black text-xl shrink-0">{initials}</div>
          <div className="min-w-0">
            <div className="font-black text-lg truncate">{sessionUser.name || 'My Account'}</div>
            <div className="text-sm text-white/80">{sessionUser.phone}</div>
          </div>
        </div>

        <Card className="mb-4">
          <InfoRow icon="👤" label="Name" value={sessionUser.name || '—'} />
          <InfoRow icon="📱" label="Mobile" value={sessionUser.phone || '—'} />
          <InfoRow icon="✉️" label="Email" value={sessionUser.email || (te ? 'ఇవ్వలేదు' : 'Not provided')} muted={!sessionUser.email} />
        </Card>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <button type="button" onClick={() => navigate('/profile/addresses')} className="bg-white rounded-2xl border border-black/5 shadow-soft p-3.5 text-left">
            <div className="h-10 w-10 rounded-xl bg-sky-100 flex items-center justify-center text-xl">📍</div>
            <div className="font-extrabold text-[13px] text-ink mt-2">{te ? 'చిరునామాలు' : 'Addresses'}</div>
          </button>
          <button type="button" onClick={() => navigate('/profile/wallet')} className="bg-white rounded-2xl border border-black/5 shadow-soft p-3.5 text-left">
            <div className="h-10 w-10 rounded-xl bg-gold-light flex items-center justify-center text-xl">💰</div>
            <div className="font-extrabold text-[13px] text-ink mt-2">{te ? 'వాలెట్' : 'Wallet'}</div>
          </button>
          <button type="button" onClick={() => navigate('/orders')} className="bg-white rounded-2xl border border-black/5 shadow-soft p-3.5 text-left">
            <div className="h-10 w-10 rounded-xl bg-berry-light flex items-center justify-center text-xl">📦</div>
            <div className="font-extrabold text-[13px] text-ink mt-2">{te ? 'ఆర్డర్లు' : 'Orders'}</div>
          </button>
        </div>

        {banners.length > 0 ? (
          <div className="mb-4">
            <div className="text-[11px] font-extrabold text-ink-faint uppercase tracking-wide mb-2">{te ? 'ఆఫర్లు' : 'Offers'}</div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {banners.map((b) => (
                <button
                  type="button"
                  key={b.id}
                  onClick={() => { if (b.link_url) window.open(b.link_url, '_blank', 'noopener,noreferrer'); }}
                  className="shrink-0 w-[85%] sm:w-72 rounded-2xl overflow-hidden shadow-soft border border-black/5 text-left"
                  title={b.description || b.title}
                >
                  <img src={fixImageUrl(b.image_url) || b.image_url} alt={b.title || 'Offer'} loading="lazy" className="w-full aspect-[16/8] object-cover" />
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {referral?.share_url ? (
          <Card className="mb-4 !bg-brand-light/50 !border-brand-light">
            <div className="font-black text-brand-dark mb-1.5">🎁 {te ? 'స్నేహితుడిని ఆహ్వానించండి' : 'Refer a friend'}</div>
            <div className="text-[13px] text-ink-soft mb-2.5 leading-relaxed">
              {referral.message || (te ? 'మీ లింక్ షేర్ చేయండి.' : 'Share your link. Earn bonus when their first order is delivered.')}
              {referral.bonus_enabled ? ` (₹${referral.bonus_amount})` : ''}
            </div>
            <div className="text-xs font-extrabold text-brand mb-2.5 tracking-widest">Code: {referral.referral_code}</div>
            <div className="flex gap-2 flex-wrap">
              <Button
                className="flex-1 min-w-[120px]"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(referral.share_url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
                  catch { window.prompt('Copy this link', referral.share_url); }
                }}
              >
                {copied ? '✓ Copied' : 'Copy link'}
              </Button>
              <Button
                variant="outline"
                className="flex-1 min-w-[120px]"
                onClick={() => {
                  const text = `Shop fresh with NRK Mart! Use my link: ${referral.share_url}`;
                  if (navigator.share) navigator.share({ title: 'NRK Mart', text, url: referral.share_url }).catch(() => {});
                  else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                }}
              >
                Share
              </Button>
            </div>
          </Card>
        ) : null}

        {!logoutConf ? (
          <Button variant="danger" fullWidth onClick={() => setLogoutConf(true)}>🚪 {te ? 'సైన్ అవుట్' : 'Sign Out'}</Button>
        ) : (
          <Card className="!bg-red-50 !border-red-200">
            <div className="font-bold text-sm text-ink mb-3">{te ? 'సైన్ అవుట్ చేయాలా?' : 'Sign out of NRK Mart?'}</div>
            <div className="flex gap-2.5">
              <Button variant="ghost" className="flex-1" onClick={() => setLogoutConf(false)}>{te ? 'రద్దు' : 'Cancel'}</Button>
              <Button variant="dangerSolid" className="flex-1" onClick={() => { onLogout(); navigate('/'); }}>{te ? 'అవును' : 'Yes, Sign Out'}</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
